import React from 'react';
import { CheckboxInput, Modal } from 'lib/components';
import { NewTextInput } from 'lib/components/inputs';
import { useOnClickOutside } from 'lib/utils';
import { PageHeader } from 'app/pages/account/userAccount/addOns/components/PageElements';
import styled, { useTheme } from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import {
  RequestVinSolutionsIntegration,
  ReqestEleadIntegration,
  RequestCodirect,
  requestAddon,
  requestAffiliateAddon,
} from 'lib/api/addonsApi';
import { useHistory } from 'react-router-dom';
import { successToast } from '../toasts/success';
import { Button } from 'react-covideo-common';
import { IoMdClose } from 'react-icons/io';

type Props = {
  handleModalClose: () => void;
  subject: string;
  title?: string;
  description?: string | React.ReactNode;
};

const Header = styled.div`
  display: flex;
`;

const HeaderTitle = styled(PageHeader)`
  font-size: 18px;

  font-weight: 700;
`;

const CloseButtonWrap = styled.div`
  margin-left: auto;
  margin-top: 24px;
  margin-right: 24px;
  color: ${({ theme }) => theme.colors.white[100]};
  height: 24px;
  width: 24px;
  &:hover {
    opacity: 0.7;
    cursor: pointer;
  }
`;

const Content = styled.div`
  margin: 0px 30px 30px 30px;
  width: 592px;

  font-weight: 400;
`;

const Note = styled.textarea`
  width: 100%;
  height: 120px;
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.neutral[40]};
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: ${({ theme }) => theme.colors.black[100]};
  &:focus {
    outline: 0;
  }
`;

const Group = styled.div`
  margin-bottom: 24px;
`;

const Row = styled.div`
  display: flex;
  margin-bottom: 10px;
  align-items: center;
  width: 100%;
  color: ${({ theme }) => theme.colors.neutral[80]};
  label {
    &:nth-child(1) {
      margin: 0 12px 0 0;
    }
  }
`;

const Textarea = styled(NewTextInput)`
  color: ${({ theme }) => theme.colors.neutral[60]};
  background-color: ${({ theme }) => theme.colors.neutral[20]};

  &:focus {
    color: ${({ theme }) => theme.colors.neutral[60]};
    background-color: ${({ theme }) => theme.colors.neutral[20]};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  width: 100%;
  place-content: flex-end;
`;

const Description = styled.div``;

export const CONTACT_AM_SUBJECTS = {
  CODIRECT: 'Codirect Request',
  ELEAD: 'Elead Integration',
  VINSOLUTION: 'VinSolutions Integration',
  PACKAGE: 'Covideo Legacy Plan Update',
  AFFILIATE: 'Affiliate Earnings Request',
  SUBSCRIPTION: 'Covideo Subscription Update Request',
  SMS: 'New Covideo Customer SMS Order',
};

export const ModalContactAM = ({
  handleModalClose,
  subject,
  title,
  description,
}: Props) => {
  const { t } = useTranslation();
  const themes = useTheme();
  const { ref } = useOnClickOutside(handleModalClose);
  const [notes, setNotes] = React.useState('');
  const [emailSelected, setEmailSelected] = React.useState(false);
  const [phoneSelected, setPhoneSelected] = React.useState(false);

  const history = useHistory();

  const SendRequest = async () => {
    if (subject === CONTACT_AM_SUBJECTS.CODIRECT) {
      await RequestCodirect({
        subject,
        notes,
        email: emailSelected,
        phone: phoneSelected,
      }).then(() => {
        successToast({
          title: t('Your request has been submitted.'),
        });

        history.push('/home');
      });
    } else if (subject === CONTACT_AM_SUBJECTS.ELEAD) {
      await ReqestEleadIntegration({
        subject,
        notes,
        email: emailSelected,
        phone: phoneSelected,
      }).then(() => {
        successToast({
          title: t('Your request has been submitted.'),
        });

        history.push('/integrations');
      });
    } else if (subject === CONTACT_AM_SUBJECTS.VINSOLUTION) {
      await RequestVinSolutionsIntegration({
        notes,
        email: emailSelected,
        phone: phoneSelected,
      }).then(() => {
        successToast({
          title: t(
            'Thanks for your interest in {{subject}}. One of our specialists will be in touch shortly.',
            { subject: 'VinSolutions Integration' }
          ),
        });
        history.push('/integrations');
      });
    } else if (subject === CONTACT_AM_SUBJECTS.PACKAGE) {
      await requestAddon({
        subject,
        notes,
        email: emailSelected,
        phone: phoneSelected,
        isBilling: true,
      });
      handleModalClose();
      history.push('/billing');
    } else if (subject === CONTACT_AM_SUBJECTS.AFFILIATE) {
      await requestAffiliateAddon({
        subject,
        notes,
        email: emailSelected,
        phone: phoneSelected,
      }).then(() => {
        successToast({
          title: t(
            'Thanks for your interest in {{subject}}. One of our specialists will be in touch shortly.',
            { subject }
          ),
        });

        history.push('/home');
      });
    } else if (subject === CONTACT_AM_SUBJECTS.SUBSCRIPTION) {
      await requestAddon({
        subject,
        notes,
        email: emailSelected,
        phone: phoneSelected,
        isBilling: true,
      }).then(() => {
        handleModalClose();
        successToast({
          title: t(
            'Thanks for your interest in {{subject}}. One of our specialists will be in touch shortly.',
            { subject }
          ),
        });
      });
    } else if (subject === CONTACT_AM_SUBJECTS.SMS) {
      await requestAddon({
        subject,
        notes,
        email: emailSelected,
        phone: phoneSelected,
        isBilling: true,
      }).then(() => {
        handleModalClose();
        successToast({
          title: t(
            'Thanks for your interest in {{subject}}. One of our specialists will be in touch shortly.',
            { subject }
          ),
        });
      });
    } else {
      await requestAddon({
        subject,
        notes,
        email: emailSelected,
        phone: phoneSelected,
      }).then(() => {
        successToast({
          title: t(
            'Thanks for your interest in {{subject}}. One of our specialists will be in touch shortly.',
            { subject }
          ),
        });

        history.push('/home');
      });
    }
  };

  return (
    <Modal>
      <div ref={ref}>
        <Header>
          <HeaderTitle>
            {title ? title : t('Contact Account Manager')}
          </HeaderTitle>
          <CloseButtonWrap title={t('Close')}>
            <IoMdClose
              size={24}
              onClick={handleModalClose}
              color={themes.colors.neutral[60]}
            />
          </CloseButtonWrap>
        </Header>
        <Content>
          {description ? (
            <>
              <Description>{description}</Description>
              <br />
            </>
          ) : null}
          <Group>
            <Row>{t('Subject')}</Row>
            <Row>
              <Textarea
                readOnly
                value={subject}
                placeholder={t('Enter subject...')}
                required={true}
              />
            </Row>
          </Group>
          <Group>
            <Row>{t('Write a note or a question for Account Manager')}</Row>
            <Row>
              <Note onChange={(e: any) => setNotes(e.target.value)}></Note>
            </Row>
          </Group>
          <Group>
            <Row>{t('Preferred method of contact')}</Row>
            <Row>
              <CheckboxInput
                name='contactEmail'
                id='contactEmail'
                checked={emailSelected}
                onChange={() => setEmailSelected(!emailSelected)}
              />
              {t('Email')}
            </Row>
            <Row>
              <CheckboxInput
                name='contactPhone'
                id='contactPhone'
                checked={phoneSelected}
                onChange={() => setPhoneSelected(!phoneSelected)}
              />
              {t('Phone')}
            </Row>
          </Group>
          <ButtonRow>
            <Button
              text={t('Send Message')}
              disabled={!(emailSelected || phoneSelected)}
              onClick={SendRequest}
            />
          </ButtonRow>
        </Content>
      </div>
    </Modal>
  );
};
